import React, { useContext, useEffect, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import axios from 'axios';
import { IGetModifierGroupsTemplateInfo, ServerErrorTypes } from 'types';
import { getModifierGroupsTemplateInfo, updateModifierGroupsTemplateInfo } from 'backend/modifierGroups';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { ModifierGroupsMultipleSelect } from './ModifierGroupsMultipleSelect';
import style from './index.module.scss';

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	onShowProductHasPortionsError: () => void;
	modifierId: string;
}

export const ModalEditNestedModGroupTemplate: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	onShowProductHasPortionsError,
	modifierId,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [modifierGroups, setModifierGroups] = useState<string[]>([]);
	const [modifierGroupsWithAdditionalData, setModifierGroupsWithAdditionalData] = useState<
		IGetModifierGroupsTemplateInfo['templates']
	>([]);
	const [responseData, setResponseData] = useState<Nullable<IGetModifierGroupsTemplateInfo>>(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				showLoader();
				const response = await getModifierGroupsTemplateInfo({ productId: modifierId, type: 'Modifier' });
				const modifierGroupIds = response.data.templates.map(
					(template: IGetModifierGroupsTemplateInfo['templates'][0]) => template.id,
				);
				setModifierGroups(modifierGroupIds);
				setModifierGroupsWithAdditionalData(response.data.templates);
				setResponseData(response.data);
			} catch (err) {
				if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.PRODUCT_HAS_PORTIONS) {
					onShowProductHasPortionsError();
				} else {
					onShowError();
				}
			} finally {
				hideLoader();
			}
		};
		fetchData();
	}, []);

	const handleAssignTemplates = async () => {
		try {
			showLoader();
			await updateModifierGroupsTemplateInfo({
				productId: modifierId,
				type: 'Modifier',
				modifierGroups: modifierGroupsWithAdditionalData,
			});
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	return (
		<>
			{responseData && (
				<ModalContent
					onConsentEvent={handleAssignTemplates}
					onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
					heading="editTemplateForNestedModGroups"
					headerTranslationNameSpace="modals"
					translationOfConfirmBtn="saveButton"
					headingAlignment="left"
					centered={false}>
					<Text
						translationKey="setNestedModGroupTemplateAndTheirPriority"
						translationNameSpace="modals"
						size="s"
					/>
					<div className={style.row}>
						<div>
							<FullInfoField label="customerLabel" />
							<Text translationKey={responseData?.customer.name} size="s" withTooltip={true} />
						</div>
						<div>
							<FullInfoField label="modifierLabel" />
							<Text translationKey={responseData?.product.name} size="s" withTooltip={true} />
						</div>
					</div>
					<ModifierGroupsMultipleSelect
						setModifierGroups={setModifierGroups}
						setModifierGroupsWithAdditionalData={setModifierGroupsWithAdditionalData}
						modifierGroups={modifierGroups}
						modifierGroupsWithAdditionalData={modifierGroupsWithAdditionalData}
						customerID={responseData?.customer.id}
					/>
				</ModalContent>
			)}
		</>
	);
};
